"use client"

import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Loader2, RefreshCw } from "lucide-react"
import { useRefreshWhatsappNumberMutation } from "@/hooks/mutations/whatsapp-refresh"
import type { WhatsappNameStatus } from "@/lib/agents/messaging/channels/whatsapp/types"
import type { WhatsappNumberRefresh } from "@/server-actions/whatsapp-connect"
import type { MessageAgentChannel } from "@/hooks/agents/messaging/use-agents"
import { useTranslations } from "next-intl"
import { WhatsappAccountReadiness } from "@/components/whatsapp-templates/account-readiness"


interface WhatsappNumberDialogProps {
  channel: MessageAgentChannel
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Reload the channel list — account_name may have changed. */
  onRefreshed: () => void
}

const NAME_STATUS_LABELS: Record<WhatsappNameStatus, string> = {
  APPROVED: "Approved",
  AVAILABLE_WITHOUT_REVIEW: "Available without review",
  DECLINED: "Declined",
  EXPIRED: "Expired",
  PENDING_REVIEW: "Pending review",
  NONE: "Not submitted",
}

const NAME_STATUS_COLORS: Record<WhatsappNameStatus, string> = {
  APPROVED: "bg-green-100 text-green-800",
  AVAILABLE_WITHOUT_REVIEW: "bg-green-100 text-green-800",
  DECLINED: "bg-red-100 text-red-800",
  EXPIRED: "bg-red-100 text-red-800",
  PENDING_REVIEW: "bg-amber-100 text-amber-800",
  NONE: "bg-gray-100 text-gray-800",
}

/** What the customer should do next, given the display-name state. */
function outcomeMessage(result: WhatsappNumberRefresh, tUi: ReturnType<typeof useTranslations<"Messaging.AgentsMessagingSettingsWhatsappNumberDialog">>): string {
  // A number that is not CONNECTED cannot send whatever its name says — diagnosing
  // the display name here would send the customer after the wrong fix.
  if (result.number_status && result.number_status !== "CONNECTED") {
    return tUi("metaReportsThisNumberAsValue0SoIt", { value0: String(result.number_status.toLowerCase().replace(/_/g, " ")) })
  }
  if (result.coexistence) {
    return tUi("thisNumberIsRegisteredThroughTheWhatsAppBusiness")
  }
  if (result.reregistered) {
    return tUi("theNumberWasRegisteredAgainWhichAppliesIts")
  }
  if (result.name_status === "PENDING_REVIEW") {
    return tUi("theDisplayNameIsStillUnderReviewBy")
  }
  if (result.name_status === "DECLINED" || result.name_status === "EXPIRED") {
    return tUi("metaDidNotApproveThisDisplayNameSubmit")
  }
  return tUi("noDisplayNameHasBeenSubmittedForThis")
}

/**
 * Number maintenance for a connected WhatsApp channel.
 *
 * Exists because Meta approves a display name without applying it: the number
 * keeps failing sends with error 131037 until it is registered again, and we
 * only ever register at connect time.
 */
export function WhatsappNumberDialog({
  channel,
  open,
  onOpenChange,
  onRefreshed,
}: WhatsappNumberDialogProps) {
  const tUi = useTranslations("Messaging.AgentsMessagingSettingsWhatsappNumberDialog")

  const [result, setResult] = useState<WhatsappNumberRefresh | null>(null)
  const refresh = useRefreshWhatsappNumberMutation()

  const handleRefresh = async () => {
    try {
      const value = await refresh.mutateAsync(channel.id)
      setResult(value)
      onRefreshed()
    } catch {
      // withQueryHandling already surfaced the error as a toast.
    }
  }

  const handleOpenChange = (next: boolean) => {
    if (!next) setResult(null)
    onOpenChange(next)
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-h-[85dvh] overflow-y-auto sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle><span key={tUi("whatsAppNumber")}>{tUi("whatsAppNumber")}</span></DialogTitle>
          <DialogDescription>
            <span key={tUi("reReadThisNumberSStatusFromMeta")}>{tUi("reReadThisNumberSStatusFromMeta")}</span></DialogDescription>
        </DialogHeader>

        <div className="space-y-4 text-sm">
          {open ? <WhatsappAccountReadiness channelId={channel.id} /> : null}
          <div className="rounded-lg border p-3 space-y-1">
            <div className="flex items-center justify-between gap-3">
              <span className="text-gray-500"><span key={tUi("displayName")}>{tUi("displayName")}</span></span>
              <span className="font-medium truncate">
                {result?.verified_name || channel.account_name || "—"}
              </span>
            </div>
            <div className="flex items-center justify-between gap-3">
              <span className="text-gray-500"><span key={tUi("phoneNumberID")}>{tUi("phoneNumberID")}</span></span>
              <span className="font-mono text-xs truncate">{channel.external_account_id || "—"}</span>
            </div>
            {result?.name_status && (
              <div className="flex items-center justify-between gap-3">
                <span className="text-gray-500"><span key={tUi("nameStatus")}>{tUi("nameStatus")}</span></span>
                <Badge className={NAME_STATUS_COLORS[result.name_status]}>
                  {NAME_STATUS_LABELS[result.name_status]}
                </Badge>
              </div>
            )}
            {/* Only worth a row when it is the problem — a CONNECTED number is the
                expected case and says nothing the user needs to act on. */}
            {result?.number_status && result.number_status !== "CONNECTED" && (
              <div className="flex items-center justify-between gap-3">
                <span className="text-gray-500"><span key={tUi("numberStatus")}>{tUi("numberStatus")}</span></span>
                <Badge className="bg-red-100 text-red-800">{result.number_status}</Badge>
              </div>
            )}
          </div>

          {result ? (
            <p className="text-gray-600">{outcomeMessage(result, tUi)}</p>
          ) : (
            <p className="text-gray-600">
              <span><span key={tUi("ifMessagesFailWithError")}>{tUi("ifMessagesFailWithError")}</span></span> <span className="font-mono">131037</span><span><span key={tUi("thisNumberHasNo")}>{tUi("thisNumberHasNo")}</span></span> <em><span key={tUi("applied")}>{tUi("applied")}</span></em> <span><span key={tUi("displayNameMetaCanApproveANameWithout")}>{tUi("displayNameMetaCanApproveANameWithout")}</span></span>
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={refresh.isPending}>
            <span key={tUi("close")}>{tUi("close")}</span></Button>
          <Button onClick={handleRefresh} disabled={refresh.isPending}>
            {refresh.isPending ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4 mr-2" />
            )}
            <span><span key={tUi("refreshNumber")}>{tUi("refreshNumber")}</span></span>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
